import { AlertTriangle, Inbox, Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from './button';

/*
 * Trạng thái Loading / Empty / Error chuẩn (Design System §10: không để màn hình trống,
 * không spinner toàn màn hình, lỗi phải nói chuyện gì – vì sao – làm gì tiếp).
 */

export const Spinner = ({ className }: { className?: string }) => <Loader2 className={cn('size-4 animate-spin', className)} />;

/** Khối xám giữ chỗ khi đang tải. */
export const Skeleton = ({ className }: { className?: string }) => <div aria-hidden="true" className={cn('animate-pulse rounded bg-muted', className)} />;

/** Đang tải: dạng khung bảng (mặc định) để giữ bố cục, hoặc dòng chữ ngắn khi `inline`. */
export function LoadingState({ label = 'Đang tải...', rows = 6, inline }: { label?: string; rows?: number; inline?: boolean }) {
  if (inline)
    return (
      <div role="status" className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
        <Spinner /> {label}
      </div>
    );
  return (
    <div role="status" aria-label={label} className="space-y-2 p-4">
      <span className="sr-only">{label}</span>
      <Skeleton className="h-8 w-full" />
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-6" />
      ))}
    </div>
  );
}

/** Trống: tiêu đề + giải thích ngắn + hành động (nút/phím tắt) qua `children`. */
export function EmptyState({ title, description, children }: { title: string; description?: ReactNode; children?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-sm text-muted-foreground">
      <Inbox className="size-8 opacity-50" aria-hidden="true" />
      <div className="font-medium text-foreground">{title}</div>
      {description && <div className="max-w-md">{description}</div>}
      {children}
    </div>
  );
}

/** Gợi ý người dùng làm gì tiếp theo loại lỗi. */
function nextStep(error: unknown): string {
  if (!(error instanceof ApiError)) return 'Tải lại trang (F5). Nếu vẫn lỗi, báo quản trị hệ thống.';
  if (error.status === 0) return 'Kiểm tra kết nối mạng rồi bấm Thử lại.';
  if (error.status === 401) return 'Phiên đăng nhập đã hết. Đăng nhập lại rồi thử lại.';
  if (error.status === 403) return 'Tài khoản của bạn chưa có quyền này. Liên hệ trưởng phòng hoặc quản trị để được cấp quyền.';
  if (error.status === 404) return 'Dữ liệu có thể đã bị xóa hoặc đường dẫn sai. Quay lại danh sách để mở lại.';
  if (error.status >= 500) return 'Máy chủ đang gặp sự cố. Thử lại sau ít phút; nếu vẫn lỗi, báo quản trị kèm mã lỗi bên dưới.';
  return 'Kiểm tra lại thông tin rồi thử lại.';
}

/** Lỗi: chuyện gì xảy ra (title) – nguyên nhân (thông báo từ máy chủ) – làm gì tiếp – [Thử lại]. */
export function ErrorState({ error, onRetry, title = 'Không tải được dữ liệu' }: { error: unknown; onRetry?: () => void; title?: string }) {
  const cause = error instanceof Error ? error.message : null;
  const ref = error instanceof ApiError ? error.requestId : undefined;
  return (
    <div role="alert" className="flex flex-col items-center justify-center gap-2 px-4 py-12 text-center text-sm">
      <AlertTriangle className="size-8 text-error" aria-hidden="true" />
      <div className="font-semibold">{title}</div>
      {cause && <div className="max-w-md text-muted-foreground">Nguyên nhân: {cause}</div>}
      <div className="max-w-md text-muted-foreground">{nextStep(error)}</div>
      {ref && <div className="font-mono text-[11px] text-muted-foreground">Mã lỗi: {ref}</div>}
      {onRetry && (
        <Button variant="outline" size="sm" className="mt-1" onClick={onRetry}>
          Thử lại
        </Button>
      )}
    </div>
  );
}
